"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import axios from "axios"
import { TodaysMiningReport } from "./components/todays-mining-report"
import { PastReports } from "./components/past-reports"
import { ProfileSidebar } from "./components/profile-sidebar"
import { ReportDetailsModal } from "./components/report-details-modal"

export default function UserDashboard() {
  const [pastReports, setPastReports] = useState([])
  const [todaysReport, setTodaysReport] = useState(null)
  const [selectedReport, setSelectedReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!user) {
      setError("Please log in to access the dashboard")
      navigate("/login")
      return
    }
    const userId = user.id
    if (!userId || userId <= 0) {
      setError("Invalid User ID")
      setLoading(false)
      navigate("/login")
      return
    }
    const fetchReports = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`https://backend.emerald.azadnoori.com/api/v1/reports/userreports/${userId}`, {
          withCredentials: true,
        })
        const formattedReports = response.data.data.reports.map((report) => ({
          id: report.id,
          date: report.date,
          caving: report.caving || 0,
          uselessStones: report.useless_stones || 0,
          emeraldsFound: report.emeralds_found || 0,
          notes: report.notes || "",
          images: report.images ? (Array.isArray(report.images) ? report.images : JSON.parse(report.images)) : [], // Changed from photos to images
          team: report.user ? [`${report.user.firstname} ${report.user.lastname}`] : ["Unknown"],
        }))
        setPastReports(formattedReports)
        const today = new Date()
        const recentReport =
          formattedReports.find((report) => new Date(report.date).toDateString() === today.toDateString()) ||
          formattedReports[0]
        setTodaysReport(recentReport)
      } catch (err) {
        if (err.response?.status === 401) {
          setError("Session expired. Please log in again.")
          navigate("/login")
        } else if (err.response?.status === 403) {
          setError("Unauthorized: You cannot access this user's reports.")
          navigate("/login")
        } else {
          setError(err.response?.data?.message || "Failed to fetch reports")
        }
      } finally {
        setLoading(false)
      }
    }
    fetchReports()
  }, [user, authLoading, navigate])

  const handleViewDetails = (report) => {
    setSelectedReport(report)
  }

  const handleCloseModal = () => {
    setSelectedReport(null)
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b shadow-sm">
        <h1 className="text-xl font-bold">User Dashboard</h1>
        <button onClick={toggleSidebar} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="4" x2="20" y1="12" y2="12" />
            <line x1="4" x2="20" y1="6" y2="6" />
            <line x1="4" x2="20" y1="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)}>
          <div className="w-[280px] h-full bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <ProfileSidebar user={user} isMobile={true} onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-6">
        <div className="lg:hidden mb-6">
          {/* Chat with Admin Button */}
          <a
            href="https://wa.me/+93729599599"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-green-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-600 transition-colors"
          >
            Chat with Admin
          </a>
        </div>

        {(loading || authLoading) && <p className="text-gray-500">Loading...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}

        {!loading && !authLoading && !error && (
          <>
            <TodaysMiningReport miningReport={todaysReport} />
            <PastReports pastReports={pastReports} formatDate={formatDate} onViewDetails={handleViewDetails} />
          </>
        )}

        <ReportDetailsModal report={selectedReport} formatDate={formatDate} onClose={handleCloseModal} />
      </div>

      {/* Desktop Sidebar */}
      <ProfileSidebar user={user} />
    </div>
  )
}
